import { throttle } from 'lodash';
import { useEffect, useRef } from 'react';
import { AppEvent, useAppPubSub } from 'contexts/app-pub-sub/AppPubSub';
import { EventsList } from 'contexts/app-pub-sub/events-list';
import { SessionStorageKeys } from 'enums';
// eslint-disable-next-line import/no-internal-modules
import { useToast } from 'hooks/useToast';
import { MismatchVersionNotification } from 'components/MismatchVersionNotification';

interface ServerVersionNotification {
  serverVersion: string;
  notificationDiscarded: boolean;
}

interface ServerVersionEvent extends AppEvent {
  data: {
    version: string;
  };
}

const getStoredVersionData = (): ServerVersionNotification | null => {
  const storedData = sessionStorage.getItem(SessionStorageKeys.ServerVersion);
  if (!storedData) {
    return null;
  }
  try {
    return JSON.parse(storedData) as ServerVersionNotification;
  } catch {
    sessionStorage.removeItem(SessionStorageKeys.ServerVersion);
    return null;
  }
};

const setStoredVersionData = (data: ServerVersionNotification): void => {
  sessionStorage.setItem(SessionStorageKeys.ServerVersion, JSON.stringify(data));
};

export const useServerVersionHandler = () => {
  const eventEmitter = useAppPubSub();
  const { showToast } = useToast();
  const versionFromTheServer = useRef<string | null>(null);

  useEffect(() => {
    // We throttle this handling to avoid unnecessary session storage access
    const handleServerVersion = throttle(
      (event: AppEvent) => {
        if (event.event !== EventsList.ServerVersionReceived) {
          return;
        }

        const serverVersion = (event as ServerVersionEvent).data.version;
        if (!serverVersion) {
          return;
        }
        versionFromTheServer.current = serverVersion;

        const storedData = getStoredVersionData();

        // If we have no stored data, store the current server version
        if (!storedData) {
          setStoredVersionData({
            serverVersion,
            notificationDiscarded: false,
          });
          return;
        }

        // If versions are the same, we don't need to do anything
        if (serverVersion === storedData?.serverVersion) {
          return;
        }

        // If the notification was discarded, don't show it again
        if (storedData.notificationDiscarded) {
          return;
        }

        // Show the notification for version mismatch
        showToast(
          'info',
          { message: <MismatchVersionNotification /> },
          {
            preventDuplicate: true,
            autoHideDuration: null,
            key: 'mismatch-version-notification',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            onClose: () => {
              // This means the naughty user discarded the notification without refreshing the page
              setStoredVersionData({
                serverVersion: storedData.serverVersion,
                notificationDiscarded: true,
              });
            },
          },
        );
      },
      5000,
      { leading: true },
    );

    // Handle page reload
    const handleBeforeUnload = () => {
      const storedData = getStoredVersionData();
      if (storedData && versionFromTheServer.current) {
        setStoredVersionData({
          serverVersion: versionFromTheServer.current,
          notificationDiscarded: false,
        });
      }
    };

    eventEmitter.addListener(handleServerVersion);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      eventEmitter.removeListener(handleServerVersion);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [eventEmitter, showToast]);
};
